import React, { useRef, useState } from "react";

interface DatePickerWheelProps {
  items: string[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  label?: string;
  isActive: boolean;
}

const DatePickerWheel: React.FC<DatePickerWheelProps> = ({
  items,
  selectedIndex,
  onSelect,
  label,
  isActive,
}) => {
  const wheelRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);

  const itemHeight = 35;

  const handleStart = (clientY: number) => {
    setIsDragging(true);
    setStartY(clientY);
    setCurrentY(0);
  };

  const handleMove = (clientY: number) => {
    if (!isDragging) return;
    const deltaY = clientY - startY;
    setCurrentY(deltaY);
  };

  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const itemsMoved = Math.round(currentY / itemHeight);
    const newIndex = Math.max(
      0,
      Math.min(items.length - 1, selectedIndex - itemsMoved)
    );
    onSelect(newIndex);
    setCurrentY(0);
  };

  const getTransform = (index: number) => {
    const offset = (index - selectedIndex) * itemHeight + currentY;
    const absOffset = Math.abs(offset);
    const scale = Math.max(0.7, 1 - absOffset / 100);
    const opacity = Math.max(0.4, 1 - absOffset / 80);

    // Check if this is the selected item
    const isSelected = index === selectedIndex && currentY === 0;

    return {
      transform: `translateY(${offset}px) scale(${scale})`,
      opacity: opacity,
      zIndex: 10 - Math.abs(index - selectedIndex),
      pointerEvents:
        Math.abs(index - selectedIndex) <= 2
          ? "auto"
          : ("none" as "auto" | "none"),
      // Add background color for selected item
      backgroundColor: isSelected ? "#f0ebeb" : "transparent",
      borderRadius: isSelected ? "8px" : "0px",
      transition: isDragging ? "none" : "all 0.3s ease",
    };
  };

  return (
    <div
      style={{
        width: "60px",
        height: "105px",
        position: "relative",
        overflow: "hidden",
        borderRadius: "10px",
        backgroundColor: "#fff",
      }}
    >
      {label && (
        <div
          style={{
            position: "absolute",
            top: "-20px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#05ACD9",
            color: "#fff",
            padding: "1px 6px",
            borderRadius: "6px",
            fontSize: "9px",
            fontWeight: "bold",
          }}
        >
          {label}
        </div>
      )}

      {/* Selection indicator background */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          height: `${itemHeight}px`,
          backgroundColor: "#F0F8FF",
          borderRadius: "8px",
          zIndex: 1,
          border: "1px solid #E3F2FD",
        }}
      />

      <div
        ref={wheelRef}
        style={{
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          touchAction: "none",
          zIndex: 2,
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          handleStart(e.touches[0].clientY);
        }}
        onTouchMove={(e) => {
          handleMove(e.touches[0].clientY);
        }}
        onTouchEnd={(e) => {
          handleEnd();
        }}
        onMouseDown={(e) => {
          handleStart(e.clientY);
        }}
        onMouseMove={(e) => {
          if (isDragging) {
            handleMove(e.clientY);
          }
        }}
        onMouseUp={(e) => {
          handleEnd();
        }}
        onMouseLeave={handleEnd}
      >
        {items.map((item, index) => {
          const isSelected = index === selectedIndex && currentY === 0;

          return (
            <div
              key={index}
              style={{
                position: "absolute",
                width: "100%",
                height: `${itemHeight}px`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: isSelected ? "black" : "#333",
                fontSize: "20px",
                fontWeight: isSelected ? "600" : "500",
                ...getTransform(index),
              }}
              onClick={() => onSelect(index)}
            >
              {item}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DatePickerWheel;
